import { useCallback, useEffect, useState } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import dayjs from "dayjs";
import * as Sentry from "@sentry/react";
import { useApi } from "../../hooks/useApi";
import { RECOVERED_AMOUNT_METER } from "../../types/billing";
import { formatCurrency } from "../../utils/formatCurrency";
import { adjustPpReason, ucFirstWord } from "../../utils/textUtils";

interface DisputeActivityBilling {
  meterEventName: string;
  value: number;
  currency: string;
  billingProfileId: number;
}

interface DisputeActivitySubmission {
  evidenceType: string | null;
  files: number;
  round: number | null;
}

interface DisputeActivityReopen {
  reason: string | null;
  oldReason: string | null;
  reasonChanged: boolean;
}

interface DisputeActivityEvent {
  action: string | null;
  oldStatus: string | null;
  newStatus: string | null;
  actorName: string | null;
  createdAt: string | null;
  dryRun: boolean;
  billing: DisputeActivityBilling | null;
  submission: DisputeActivitySubmission | null;
  reopen: DisputeActivityReopen | null;
}

const ACTION_LABELS: Record<string, string> = {
  assign_to_me: "Self-assigned",
  assign_to_user: "Assignment updated",
  account_download: "Account data downloaded",
  initial_load: "Dispute imported",
  webhook_dispute_created: "Dispute opened",
  document_uploaded: "Document uploaded",
  document_deleted: "Document deleted",
  paypal_status_recalculated: "PayPal status recalculated",
  loop_auto_cancel_succeeded: "Subscription auto-cancelled (Loop)",
  loop_auto_cancel_already_cancelled: "Loop: subscription already cancelled",
  loop_auto_cancel_no_subscription: "Loop: no subscription found",
  loop_auto_cancel_skipped: "Loop auto-cancel skipped",
  loop_auto_cancel_failed: "Loop auto-cancel failed",
  kaching_auto_cancel_succeeded: "Subscription auto-cancelled (Kaching)",
  kaching_auto_cancel_already_cancelled: "Kaching: contract already cancelled",
  kaching_auto_cancel_no_subscription: "Kaching: no contract found",
  kaching_auto_cancel_skipped: "Kaching auto-cancel skipped",
  kaching_auto_cancel_failed: "Kaching auto-cancel failed",
  coc_chargeback_marked: "CoC chargeback marked",
  coc_chargeback_unmatched: "CoC match not found",
  coc_chargeback_ambiguous: "CoC match ambiguous",
  coc_chargeback_failed: "CoC chargeback marking failed",
  crm_chargeback_marked: "CRM chargeback recorded",
  crm_chargeback_unmatched: "CRM match not found",
  crm_chargeback_ambiguous: "CRM match ambiguous",
  crm_chargeback_failed: "CRM chargeback recording failed",
  coc_external_refund_marked: "CoC refunded externally",
  coc_external_refund_already_refunded: "CoC: already refunded",
  coc_external_refund_unmatched: "CoC match not found",
  coc_external_refund_ambiguous: "CoC match ambiguous",
  coc_external_refund_needs_review: "CoC external refund needs review",
  coc_external_refund_pending_reversal: "CoC reversal pending settlement",
  coc_external_refund_inspection_failed: "CoC refund inspection failed",
  coc_external_refund_failed: "CoC external refund failed",
  coc_subscription_cancelled: "CoC subscription cancelled",
  coc_subscription_already_cancelled: "CoC subscription already cancelled",
  coc_subscription_not_subscription: "CoC order not a subscription",
  coc_subscription_cancel_ambiguous: "CoC subscription match ambiguous",
  coc_subscription_cancel_failed: "CoC subscription cancel failed",
  dispute_billed: "Billed",
  evidence_submitted: "Evidence submitted",
  paypal_dispute_reopened: "PayPal reopened the case",
};

const ASSIGN_ACTIONS = new Set(["assign_to_me", "assign_to_user"]);

const DOT_COLORS: Record<string, string> = {
  coc_chargeback_marked: "#12b76a",
  coc_chargeback_failed: "#f04438",
  crm_chargeback_marked: "#12b76a",
  crm_chargeback_failed: "#f04438",
  coc_external_refund_marked: "#12b76a",
  coc_external_refund_already_refunded: "#12b76a",
  coc_external_refund_failed: "#f04438",
  coc_subscription_cancelled: "#12b76a",
  coc_subscription_already_cancelled: "#12b76a",
  coc_subscription_cancel_failed: "#f04438",
  paypal_dispute_reopened: "#f04438",
};

function billingDetail(billing: DisputeActivityBilling): string {
  const billed = billing.meterEventName === RECOVERED_AMOUNT_METER
    ? formatCurrency(billing.value, billing.currency, false)
    : `${billing.value} dispute${billing.value === 1 ? "" : "s"}`;
  return `${billing.meterEventName} · ${billed} · billing profile ${billing.billingProfileId}`;
}

function submissionDetail(submission: DisputeActivitySubmission): string {
  return [
    submission.evidenceType && ucFirstWord(submission.evidenceType),
    `${submission.files} file${submission.files === 1 ? "" : "s"}`,
    submission.round !== null && submission.round > 1 && `round ${submission.round}`,
  ].filter(Boolean).join(" · ");
}

function reopenDetail({ reason, oldReason, reasonChanged }: DisputeActivityReopen): string | null {
  if (!reason) return null;
  if (oldReason) return `Reason changed: ${adjustPpReason(oldReason)} → ${adjustPpReason(reason)}`;
  return reasonChanged ? `Reason changed to ${adjustPpReason(reason)}` : adjustPpReason(reason);
}

function actionLabel(action: string | null): string {
  if (!action) return "Activity";
  return ACTION_LABELS[action] ?? action.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

export default function DisputeActivity({ disputeId, open, version = 0 }: { disputeId: number; open: boolean; version?: number }) {
  const { get, loading } = useApi();
  const [events, setEvents] = useState<DisputeActivityEvent[] | null>(null);
  const [error, setError] = useState(false);

  const fetchActivity = useCallback(async () => {
    setError(false);
    try {
      const res = await get<{ events: DisputeActivityEvent[] }>(`/api/disputes/${disputeId}/history`);
      setEvents(res.events);
    } catch (err) {
      Sentry.captureException(err);
      setError(true);
    }
  }, [get, disputeId]);

  // A document upload writes a history event while the row is open; version bumps
  // invalidate the cached load so the effect below refetches.
  useEffect(() => {
    if (version > 0) {
      setEvents(null);
      setError(false);
    }
  }, [version]);

  useEffect(() => {
    // Don't retry a failed fetch in-place; the row unmounts on collapse, so reopening it refetches.
    if (open && events === null && !error) fetchActivity();
  }, [open, events, error, fetchActivity]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">Loading activity…</Typography>
      </Box>
    );
  }
  if (error) {
    return <Typography variant="body2" color="error">Couldn&apos;t load activity — reopen the row to retry.</Typography>;
  }
  if (events && events.length === 0) {
    return <Typography variant="body2" color="text.secondary">No recorded activity yet.</Typography>;
  }
  if (!events) return null;

  return (
    <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0 }}>
      {events.map((e, i) => {
        const isLast = i === events.length - 1;
        const reopen = e.reopen && reopenDetail(e.reopen);
        return (
          <Box component="li" key={i} sx={{ display: "flex", gap: 1.5, pb: isLast ? 0 : 2 }}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", mt: "5px", flexShrink: 0, backgroundColor: (e.dryRun ? undefined : DOT_COLORS[e.action ?? ""]) ?? (ASSIGN_ACTIONS.has(e.action ?? "") ? "#465fff" : "#98a2b3") }} />
              {!isLast && <Box sx={{ flex: 1, width: "1px", backgroundColor: "divider", my: 0.5 }} />}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{actionLabel(e.action)}{e.dryRun ? " (dry run)" : ""}</Typography>
              {e.oldStatus && e.newStatus && e.oldStatus !== e.newStatus && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {e.oldStatus} → {e.newStatus}
                </Typography>
              )}
              {e.billing && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {billingDetail(e.billing)}
                </Typography>
              )}
              {e.submission && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {submissionDetail(e.submission)}
                </Typography>
              )}
              {reopen && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {reopen}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {[e.actorName, e.createdAt ? dayjs(e.createdAt).format("MMM D, YYYY h:mm A") : null].filter(Boolean).join(" · ")}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
